/**
 * DashboardFilters
 *
 * Renders a filter bar for dynamic dashboards.
 *
 * Features:
 * - Time-range presets (Today, 7 days, 30 days, 90 days, Custom)
 * - Custom date-range pickers (start / end)
 * - Dynamic text/number filters detected from MCP tool call parameters
 * - Emits a flat `filterOverrides` map whenever any filter changes
 */
import { useState, useEffect, useCallback } from "react";
import { format, subDays, startOfDay, endOfDay } from "date-fns";
import { CalendarIcon, RefreshCw, Filter } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { cn } from "@/lib/utils";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface SavedToolCall {
  tool_name: string;
  tool_input: Record<string, unknown>;
}

export interface DynamicFilter {
  /** Unique key within this dashboard (e.g. "0:start_date") */
  id: string;
  /** Human-readable label */
  label: string;
  /** Which tool call index this param belongs to */
  toolCallIndex: number;
  /** The param key inside tool_input */
  paramKey: string;
  /** Detected type */
  type: "date" | "number" | "text";
  /** Current raw value from tool_input */
  originalValue: unknown;
}

export type TimePreset = "today" | "7d" | "30d" | "90d" | "custom";

export interface FilterState {
  timePreset: TimePreset;
  startDate: Date | undefined;
  endDate: Date | undefined;
  /** Override values for dynamic non-date filters, keyed by DynamicFilter.id */
  customValues: Record<string, string>;
}

export interface FilterOverrides {
  /** Flat map of paramKey → value, ready to spread over tool_input */
  [paramKey: string]: unknown;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Param key patterns that indicate a start/from date. */
const START_DATE_PATTERNS = [
  /^(start|from|begin|since|after)[-_]?date$/i,
  /^(start|from|begin|since|after)[-_]?at$/i,
  /^date[-_]?(start|from|begin)$/i,
  /^start$/i,
  /^from$/i,
  /^since$/i,
];

/** Param key patterns that indicate an end/to date. */
const END_DATE_PATTERNS = [
  /^(end|to|until|before|thru|through)[-_]?date$/i,
  /^(end|to|until|before)[-_]?at$/i,
  /^date[-_]?(end|to|until)$/i,
  /^end$/i,
  /^to$/i,
  /^until$/i,
];

/** Generic date param that is neither clearly start nor end. */
const GENERIC_DATE_PATTERNS = [/^date$/i, /^timestamp$/i, /date/i, /^days$/i, /^period$/i];

/** Numeric param patterns (won't be shown as date pickers). */
const NUMERIC_PATTERNS = [/^(limit|count|top|n|max|min|page|offset|size)$/i, /^num/i, /^max_/i, /^min_/i];

function isDateParam(key: string): "start" | "end" | "generic" | false {
  if (START_DATE_PATTERNS.some((p) => p.test(key))) return "start";
  if (END_DATE_PATTERNS.some((p) => p.test(key))) return "end";
  if (GENERIC_DATE_PATTERNS.some((p) => p.test(key))) return "generic";
  return false;
}

function isNumericParam(key: string, value: unknown): boolean {
  if (NUMERIC_PATTERNS.some((p) => p.test(key))) return true;
  return typeof value === "number";
}

function toLabel(key: string): string {
  return key
    .replace(/[-_]/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function isoDate(d: Date): string {
  return format(d, "yyyy-MM-dd");
}

/**
 * Analyse tool calls and return the list of detected dynamic filters.
 * We surface start/end date params as part of the unified time filter and
 * everything else as individual custom filters.
 */
export function detectFilters(toolCalls: SavedToolCall[]): {
  hasStartDate: boolean;
  hasEndDate: boolean;
  startDateKeys: { toolCallIndex: number; paramKey: string }[];
  endDateKeys: { toolCallIndex: number; paramKey: string }[];
  dynamicFilters: DynamicFilter[];
} {
  const startDateKeys: { toolCallIndex: number; paramKey: string }[] = [];
  const endDateKeys: { toolCallIndex: number; paramKey: string }[] = [];
  const dynamicFilters: DynamicFilter[] = [];

  toolCalls.forEach((tc, tcIndex) => {
    Object.entries(tc.tool_input).forEach(([key, value]) => {
      const dateKind = isDateParam(key);
      if (dateKind === "start") {
        startDateKeys.push({ toolCallIndex: tcIndex, paramKey: key });
        return;
      }
      if (dateKind === "end") {
        endDateKeys.push({ toolCallIndex: tcIndex, paramKey: key });
        return;
      }
      if (dateKind === "generic") {
        // Treat as start date
        startDateKeys.push({ toolCallIndex: tcIndex, paramKey: key });
        return;
      }
      // Non-date param → dynamic filter
      const type: DynamicFilter["type"] = isNumericParam(key, value) ? "number" : "text";
      dynamicFilters.push({
        id: `${tcIndex}:${key}`,
        label: toLabel(key),
        toolCallIndex: tcIndex,
        paramKey: key,
        type,
        originalValue: value,
      });
    });
  });

  return {
    hasStartDate: startDateKeys.length > 0,
    hasEndDate: endDateKeys.length > 0,
    startDateKeys,
    endDateKeys,
    dynamicFilters,
  };
}

/**
 * Convert the current FilterState into a flat overrides map that can be
 * spread over each tool_input before re-calling the MCP tools.
 */
export function buildFilterOverrides(
  filterState: FilterState,
  startDateKeys: { toolCallIndex: number; paramKey: string }[],
  endDateKeys: { toolCallIndex: number; paramKey: string }[],
  dynamicFilters: DynamicFilter[],
): FilterOverrides {
  const overrides: FilterOverrides = {};

  // Apply date range to all detected start/end keys
  if (filterState.startDate) {
    const v = isoDate(filterState.startDate);
    startDateKeys.forEach(({ paramKey }) => { overrides[paramKey] = v; });
  }
  if (filterState.endDate) {
    const v = isoDate(filterState.endDate);
    endDateKeys.forEach(({ paramKey }) => { overrides[paramKey] = v; });
  }

  // Apply custom filter values
  dynamicFilters.forEach((f) => {
    const raw = filterState.customValues[f.id];
    if (raw === undefined || raw === "") return;
    overrides[f.paramKey] = f.type === "number" ? Number(raw) : raw;
  });

  return overrides;
}

function presetDates(preset: TimePreset): { start: Date | undefined; end: Date | undefined } {
  const now = new Date();
  if (preset === "today") return { start: startOfDay(now), end: endOfDay(now) };
  if (preset === "7d") return { start: startOfDay(subDays(now, 6)), end: endOfDay(now) };
  if (preset === "30d") return { start: startOfDay(subDays(now, 29)), end: endOfDay(now) };
  if (preset === "90d") return { start: startOfDay(subDays(now, 89)), end: endOfDay(now) };
  return { start: undefined, end: undefined };
}

// ─── Component ────────────────────────────────────────────────────────────────

interface DashboardFiltersProps {
  toolCalls: SavedToolCall[];
  onRefresh: (overrides: FilterOverrides) => void;
  refreshing: boolean;
}

export const DashboardFilters = ({ toolCalls, onRefresh, refreshing }: DashboardFiltersProps) => {
  const { hasStartDate, hasEndDate, startDateKeys, endDateKeys, dynamicFilters } = detectFilters(toolCalls);
  const hasDateFilter = hasStartDate || hasEndDate;

  const [filterState, setFilterState] = useState<FilterState>(() => ({
    timePreset: "30d",
    ...(() => {
      const { start, end } = presetDates("30d");
      return { startDate: start, endDate: end };
    })(),
    customValues: {},
  }));

  const buildOverrides = useCallback(
    (state: FilterState) =>
      buildFilterOverrides(state, startDateKeys, endDateKeys, dynamicFilters),
    [startDateKeys, endDateKeys, dynamicFilters],
  );

  // Auto-refresh when filter state changes (debounced via useEffect)
  const [pendingRefresh, setPendingRefresh] = useState(false);
  useEffect(() => {
    if (!pendingRefresh) return;
    const t = setTimeout(() => {
      setPendingRefresh(false);
      onRefresh(buildOverrides(filterState));
    }, 600);
    return () => clearTimeout(t);
  }, [pendingRefresh, filterState, buildOverrides, onRefresh]);

  const triggerRefresh = (newState: FilterState) => {
    setFilterState(newState);
    setPendingRefresh(true);
  };

  const handlePresetChange = (preset: string) => {
    const p = preset as TimePreset;
    const { start, end } = presetDates(p);
    triggerRefresh({ ...filterState, timePreset: p, startDate: start, endDate: end });
  };

  const handleStartDate = (d: Date | undefined) => {
    triggerRefresh({ ...filterState, timePreset: "custom", startDate: d });
  };

  const handleEndDate = (d: Date | undefined) => {
    triggerRefresh({ ...filterState, timePreset: "custom", endDate: d });
  };

  const handleCustomValue = (filterId: string, value: string) => {
    const newState: FilterState = {
      ...filterState,
      customValues: { ...filterState.customValues, [filterId]: value },
    };
    triggerRefresh(newState);
  };

  if (!hasDateFilter && dynamicFilters.length === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-2 border-b border-border bg-muted/30 px-4 py-2">
      <Filter className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />

      {/* ── Time range ─────────────────────────────────────────────────────── */}
      {hasDateFilter && (
        <>
          <Select value={filterState.timePreset} onValueChange={handlePresetChange}>
            <SelectTrigger className="h-7 w-32 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="today">Today</SelectItem>
              <SelectItem value="7d">Last 7 days</SelectItem>
              <SelectItem value="30d">Last 30 days</SelectItem>
              <SelectItem value="90d">Last 90 days</SelectItem>
              <SelectItem value="custom">Custom range</SelectItem>
            </SelectContent>
          </Select>

          {/* Start date picker */}
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn("h-7 gap-1.5 px-2 text-xs", !filterState.startDate && "text-muted-foreground")}
              >
                <CalendarIcon className="h-3 w-3" />
                {filterState.startDate ? format(filterState.startDate, "MMM d, yyyy") : "From"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={filterState.startDate}
                onSelect={handleStartDate}
                initialFocus
              />
            </PopoverContent>
          </Popover>

          <span className="text-xs text-muted-foreground">–</span>

          {/* End date picker */}
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn("h-7 gap-1.5 px-2 text-xs", !filterState.endDate && "text-muted-foreground")}
              >
                <CalendarIcon className="h-3 w-3" />
                {filterState.endDate ? format(filterState.endDate, "MMM d, yyyy") : "To"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={filterState.endDate}
                onSelect={handleEndDate}
                disabled={(d) => filterState.startDate ? d < filterState.startDate : false}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </>
      )}

      {/* ── Dynamic non-date filters ───────────────────────────────────────── */}
      {dynamicFilters.map((f) => (
        <div key={f.id} className="flex items-center gap-1.5">
          <Label htmlFor={`df-${f.id}`} className="text-xs text-muted-foreground whitespace-nowrap">
            {f.label}
          </Label>
          <Input
            id={`df-${f.id}`}
            type={f.type === "number" ? "number" : "text"}
            value={filterState.customValues[f.id] ?? String(f.originalValue ?? "")}
            onChange={(e) => handleCustomValue(f.id, e.target.value)}
            className="h-7 w-24 text-xs"
          />
        </div>
      ))}

      {/* ── Manual refresh button ──────────────────────────────────────────── */}
      <Button
        variant="ghost"
        size="sm"
        className="ml-auto h-7 gap-1.5 px-2 text-xs"
        disabled={refreshing}
        onClick={() => onRefresh(buildOverrides(filterState))}
        title="Refresh dashboard data"
      >
        <RefreshCw className={cn("h-3.5 w-3.5", refreshing && "animate-spin")} />
        {refreshing ? "Refreshing…" : "Refresh"}
      </Button>
    </div>
  );
};

export default DashboardFilters;
